<template>
  <div class="customer-profile-container bg-[#f4f6fc] min-h-[55vh] flex flex-col font-sans">
    <!-- Header -->
    <!-- <div class="">
      <span class="text-xl font-bold text-gray-800">Customer Details</span>
      <v-btn icon @click="handleClose">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div> -->

    <!-- Content body -->
    <div class="p-6 flex-grow">
      <v-row>
        <!-- Left Sidebar: Profile Summary -->
        <v-col cols="12" md="3">
          <v-card class="pa-4 text-center rounded-xl mb-6 shadow-sm border-0" elevation="0">
            <div class="flex justify-center mb-4 mt-2">
              <v-avatar size="120" class="border-4 border-[#e3f2fd] shadow-sm">
                <img v-if="avatarUrl" :src="avatarUrl" @error="onAvatarError" alt="Avatar" />
                <img v-else :src="fallbackAvatar" alt="Avatar" />
              </v-avatar>
            </div>
            <h2 class="text-lg font-bold text-gray-800 uppercase tracking-wide">
              {{
                customer.customerFullName ||
                `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
                'N/A'
              }}
            </h2>

            <!-- Dynamic Status Summary Chips -->
            <div v-if="Object.keys(statusCounts).length > 0" class="mt-6 border-t pt-4">
              <div class="flex items-center justify-between mb-3 px-1">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest"
                  >Loan Summary</span
                >
                <span
                  class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700"
                  >Total: {{ totalLoansCount }}</span
                >
              </div>
              <div class="flex flex-wrap justify-center gap-2 px-1">
                <v-chip
                  v-for="(count, status) in statusCounts"
                  :key="status"
                  x-small
                  label
                  class="font-black px-2"
                  :color="getStatusInfo(status).color"
                  dark
                >
                  <v-icon x-small left>{{ getStatusInfo(status).icon }}</v-icon>
                  {{ status }}: {{ count }}
                </v-chip>
              </div>
            </div>
            <!-- <div class="text-sm font-semibold text-blue-600 mt-1 uppercase tracking-wider">
              INDIVIDUAL
            </div> -->
          </v-card>
        </v-col>

        <!-- Right Content: Details -->
        <v-col cols="12" md="9">
          <!-- Navigation Tabs -->
          <v-card elevation="0" class="rounded-xl mb-6 shadow-sm border-0 overflow-hidden">
            <v-tabs
              v-model="activeTab"
              color="primary"
              active-class="font-bold text-blue-700"
              slider-size="3"
              show-arrows
            >
              <v-tab class="normal-case tracking-normal">Basic Info</v-tab>
              <v-tab class="normal-case tracking-normal">Communication</v-tab>
              <v-tab class="normal-case tracking-normal">Employment And Income</v-tab>
              <v-tab class="normal-case tracking-normal">Identity Details</v-tab>
              <v-tab class="normal-case tracking-normal">Bank Details</v-tab>
              <v-tab class="normal-case tracking-normal">Case History</v-tab>
            </v-tabs>
          </v-card>

          <!-- Tab Contents -->
          <v-tabs-items v-model="activeTab" class="bg-transparent">
            <!-- 0: Basic Info -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6">
                <v-row class="text-[#333]">
                  <v-col cols="12" sm="6">
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">CIF ID</div>
                      <div>{{ customer.crn || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Date Of Birth</div>
                      <div>{{ formatDate(customer.dateOfBirth) }}</div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Mobile Number</div>
                      <div>{{ mask(customer.customerMobile?.[0]) || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Created By</div>
                      <div class="text-gray-700">
                        {{ formatUserName(customer.createdBy) }}
                      </div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Created On</div>
                      <div class="text-gray-700">{{ formatDateTime(customer.createdAt) }}</div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Name</div>
                      <div class="uppercase">{{ customer.customerFullName || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Age</div>
                      <div>{{ calculateAge(customer.dateOfBirth) }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Gender</div>
                      <div>{{ convertTitle(customer.gender) || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Father/Spouse Name</div>
                      <div class="uppercase">{{ customer.fatherName || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Updated By</div>
                      <div class="text-gray-700">
                        {{ formatUserName(customer.updatedBy) }}
                      </div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Updated On</div>
                      <div class="text-gray-700">{{ formatDateTime(customer.updatedAt) }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- 1: Communication -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Contact Info</h4>
                <v-row class="mb-6">
                  <v-col cols="12" sm="6">
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-3">
                      <div class="font-bold text-gray-600">
                        <v-icon small color="primary" class="mr-1">mdi-email</v-icon> Email
                      </div>
                      <div class="break-words">{{ customer.customerEmail || 'N/A' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div
                      v-for="(num, idx) in customer.customerMobile"
                      :key="idx"
                      class="grid grid-cols-[140px_1fr] gap-4 mb-3"
                    >
                      <div class="font-bold text-gray-600">
                        <v-icon small color="primary" class="mr-1">mdi-phone</v-icon> Mobile
                        {{ idx + 1 }}
                      </div>
                      <div>{{ mask(num) }}</div>
                    </div>
                    <div
                      v-if="!customer.customerMobile || customer.customerMobile.length === 0"
                      class="text-gray-500 italic"
                    >
                      No mobile numbers provided.
                    </div>
                  </v-col>
                </v-row>

                <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Addresses</h4>
                <v-row v-if="customer.customerAddress && customer.customerAddress.length > 0">
                  <v-col
                    cols="12"
                    md="6"
                    v-for="address in customer.customerAddress"
                    :key="address._id"
                  >
                    <div class="border rounded-lg p-4 bg-gray-50 h-full">
                      <div class="font-bold text-primary mb-3 flex items-center justify-between">
                        <span>{{ convertTitle(address.typeOfAddress) }}</span>
                        <v-btn icon small color="blue" @click="openGoogleMaps(address)"
                          ><v-icon>mdi-map-marker-radius</v-icon></v-btn
                        >
                      </div>
                      <div class="text-gray-700 leading-relaxed">{{ extractAddress(address) }}</div>
                    </div>
                  </v-col>
                </v-row>
                <div v-else class="text-gray-500 italic">
                  No addresses registered for this customer.
                </div>
              </v-card>
            </v-tab-item>

            <!-- 2: Employment and Income -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <div v-if="customer.employmentDetails">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Job Type</div>
                        <div class="text-gray-700">
                          {{ convertTitle(customer.employmentDetails.empType) || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Employer Name</div>
                        <div class="text-gray-700 uppercase">
                          {{ customer.employmentDetails.workHistory?.[0]?.employerName || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Designation</div>
                        <div class="text-gray-700">
                          {{ customer.employmentDetails.workHistory?.[0]?.designation || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Experience</div>
                        <div class="text-gray-700">
                          {{
                            customer.employmentDetails.totalWorkExperience
                              ? `${customer.employmentDetails.totalWorkExperience} Years`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Joining Date</div>
                        <div class="text-gray-700">
                          {{ formatDate(customer.employmentDetails.joiningDate) }}
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Monthly Income</div>
                        <div class="text-blue-700 font-bold">
                          {{
                            customer.employmentDetails.monthlyIncome
                              ? `₹ ${Number(
                                  customer.employmentDetails.monthlyIncome
                                ).toLocaleString()}`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">EMI Outgo</div>
                        <div class="text-red-600 font-bold">
                          {{
                            customer.employmentDetails.totalEMIAmountPerMonth
                              ? `₹ ${Number(
                                  customer.employmentDetails.totalEMIAmountPerMonth
                                ).toLocaleString()}`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4 border-t pt-4">
                        <div class="font-bold text-gray-800">Net Take Home</div>
                        <div class="text-green-700 font-bold text-lg">
                          {{
                            customer.employmentDetails.totalIncome
                              ? `₹ ${Number(
                                  customer.employmentDetails.totalIncome
                                ).toLocaleString()}`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Employer Address Section -->
                  <div v-if="customer.employmentDetails.contact?.address" class="mt-6">
                    <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Employer Address</h4>
                    <div class="border rounded-lg p-4 bg-gray-50">
                      <div class="text-gray-700 leading-relaxed">
                        {{ extractAddress(customer.employmentDetails.contact.address) }}
                      </div>
                    </div>
                  </div>
                </div>

                <v-row v-else>
                  <v-col cols="12" md="6">
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Job Type</div>
                      <div class="text-gray-700">{{ convertTitle(customer.jobType) || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Occupation</div>
                      <div class="text-gray-700">{{ customer.occupation || 'N/A' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Company Name</div>
                      <div class="text-gray-700">{{ customer.company || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4 bg-green-50 p-2 rounded">
                      <div class="font-bold text-gray-800">Monthly Salary</div>
                      <div class="text-green-700 font-bold tracking-wide">
                        {{
                          customer.salary ? `₹ ${Number(customer.salary).toLocaleString()}` : 'N/A'
                        }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- 3: Identity Details -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <!-- Header row with View/Hide toggle -->
                <div class="flex items-center justify-between mb-4">
                  <span class="text-sm font-bold text-gray-600 uppercase tracking-wide"
                    >Identity Documents</span
                  >
                  <v-btn
                    v-if="customer.identityDetails && customer.identityDetails.length > 0"
                    small
                    :color="identityRevealed ? 'grey darken-1' : 'primary'"
                    outlined
                    :loading="loadingIdentity"
                    class="normal-case rounded-lg font-bold"
                    @click="toggleIdentityReveal"
                  >
                    <v-icon left small>{{
                      identityRevealed ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                    }}</v-icon>
                    {{ identityRevealed ? 'Hide ID Numbers' : 'View ID Numbers' }}
                  </v-btn>
                </div>

                <div v-if="customer.identityDetails && customer.identityDetails.length > 0">
                  <v-row>
                    <v-col cols="12" v-for="(id, idx) in customer.identityDetails" :key="idx">
                      <div
                        class="border rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-all"
                      >
                        <v-row align="center">
                          <v-col cols="12" :sm="id.docName && id.docType ? 8 : 12">
                            <div class="flex items-center mb-3">
                              <v-icon color="primary" class="mr-2"
                                >mdi-card-account-details-outline</v-icon
                              >
                              <span class="text-lg font-bold text-gray-800">{{ id.idType }}</span>
                              <v-chip
                                small
                                class="ml-3 font-bold"
                                :color="id.isVerfied ? 'success' : 'warning'"
                                text-color="white"
                              >
                                {{ id.isVerfied ? 'VERIFIED' : 'PENDING VERIFICATION' }}
                                <v-icon right x-small>{{
                                  id.isVerfied ? 'mdi-check-decagram' : 'mdi-alert-circle-outline'
                                }}</v-icon>
                              </v-chip>
                              <v-btn
                                v-if="!id.isVerfied"
                                small
                                color="primary"
                                class="ml-3 normal-case font-semibold rounded-lg"
                                :loading="verifyingIndex === idx"
                                @click="verifyIdentity(idx)"
                              >
                                <v-icon left small>mdi-shield-check-outline</v-icon>
                                Verify
                              </v-btn>
                            </div>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >ID Number</span
                                >
                                <!-- Show decrypted number if revealed, else show 2 initials + dots + 2 last digits -->
                                <span
                                  v-if="identityRevealed && getRevealedId(id.idType)"
                                  class="text-gray-900 font-mono font-semibold tracking-widest"
                                  >{{ getRevealedId(id.idType) }}</span
                                >
                                <span v-else class="text-gray-700 font-mono font-medium tracking-wider"
                                  >{{ formatMaskedId(id.idNumber) }}</span
                                >
                              </div>
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >Name on ID</span
                                >
                                <span class="text-gray-800 font-medium">{{
                                  id.nameAsperId ||
                                  customer.customerFullName ||
                                  `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
                                  'N/A'
                                }}</span>
                              </div>
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >Issued By</span
                                >
                                <span class="text-gray-800 font-medium">{{
                                  id.issuedBy || 'N/A'
                                }}</span>
                              </div>
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >Validity</span
                                >
                                <span class="text-gray-800 font-medium">
                                  {{ formatDate(id.issueDate) }} to {{ formatDate(id.expiryDate) }}
                                </span>
                              </div>
                            </div>

                            <div
                              v-if="id.isVerfied"
                              class="mt-4 pt-3 border-t flex flex-wrap gap-4 text-xs text-green-700 bg-green-50 p-2 rounded-lg"
                            >
                              <div class="flex items-center">
                                <v-icon x-small color="success" class="mr-1"
                                  >mdi-account-check</v-icon
                                >
                                <span
                                  >Verified by:
                                  <strong>{{ id.verifiedBy || 'System' }}</strong></span
                                >
                              </div>
                              <div class="flex items-center">
                                <v-icon x-small color="success" class="mr-1"
                                  >mdi-calendar-check</v-icon
                                >
                                <span
                                  >Verified on:
                                  <strong>{{ formatDate(id.verifiedOn) }}</strong></span
                                >
                              </div>
                            </div>
                          </v-col>

                          <!-- Document Preview Section (Identity-wise) -->
                          <v-col v-if="id.docName && id.docType" cols="12" sm="4">
                            <div
                              class="border rounded-xl p-3 bg-gray-50 flex flex-col h-full justify-between"
                            >
                              <div>
                                <div class="flex items-center justify-between mb-2">
                                  <span
                                    class="text-[10px] font-bold text-gray-500 uppercase tracking-wide"
                                  >
                                    {{ id.docType }}
                                  </span>
                                </div>
                                <div class="flex items-center space-x-2 mb-2">
                                  <v-icon
                                    size="32"
                                    :color="getFileIconInfo(getDocExt(id.docName)).color"
                                  >
                                    {{ getFileIconInfo(getDocExt(id.docName)).icon }}
                                  </v-icon>
                                  <div class="overflow-hidden flex-grow">
                                    <div
                                      class="text-xs font-semibold text-gray-800 truncate"
                                      v-tooltip="{ text: id.docName }"
                                    >
                                      {{ id.docName }}
                                    </div>
                                    <div class="text-[10px] text-gray-500 uppercase font-mono">
                                      {{ getDocExt(id.docName) }} File
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="mt-1">
                                <div
                                  v-if="isImageFile(getDocExt(id.docName))"
                                  class="relative overflow-hidden rounded-lg border bg-gray-100 aspect-video mb-2 flex items-center justify-center cursor-pointer group"
                                  @click="openImage(getCustomerDocUrl(id))"
                                >
                                  <v-img
                                    :src="getCustomerDocUrl(id)"
                                    max-height="80"
                                    contain
                                    class="transition-transform group-hover:scale-105"
                                  >
                                    <template v-slot:placeholder>
                                      <v-row
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                      >
                                        <v-progress-circular
                                          indeterminate
                                          color="grey lighten-4"
                                          size="20"
                                        ></v-progress-circular>
                                      </v-row>
                                    </template>
                                  </v-img>
                                  <div
                                    class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold"
                                  >
                                    <v-icon dark x-small class="mr-1">mdi-eye</v-icon> View
                                  </div>
                                </div>
                                <div
                                  v-else-if="getDocExt(id.docName) === 'pdf'"
                                  class="border rounded-lg bg-red-50/50 p-2 mb-2 flex items-center justify-between"
                                >
                                  <span class="text-[10px] text-red-700 font-semibold">PDF</span>
                                  <v-btn
                                    x-small
                                    color="error"
                                    class="rounded-lg font-bold normal-case text-[10px] py-1 px-2"
                                    @click="openImage(getCustomerDocUrl(id))"
                                  >
                                    <v-icon left x-small>mdi-open-in-new</v-icon> Open
                                  </v-btn>
                                </div>
                                <div
                                  v-else
                                  class="border rounded-lg bg-gray-50 p-2 mb-2 flex items-center justify-between"
                                >
                                  <span class="text-[10px] text-gray-600 font-semibold">Doc</span>
                                  <v-btn
                                    x-small
                                    color="primary"
                                    class="rounded-lg font-bold normal-case text-[10px] py-1 px-2"
                                    @click="openImage(getCustomerDocUrl(id))"
                                  >
                                    <v-icon left x-small>mdi-download</v-icon> Download
                                  </v-btn>
                                </div>
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                </div>
                <div
                  v-else
                  class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <v-icon size="64" color="gray-300">mdi-id-card</v-icon>
                  <p class="mt-4 text-gray-500 font-medium italic">
                    No identity documents provided.
                  </p>
                </div>
              </v-card>
            </v-tab-item>

            <!-- 4: Bank Details -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <div v-if="customer.bankDetails">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Bank Name</div>
                        <div class="text-gray-700 uppercase">
                          {{ customer.bankDetails.bankName || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Account Number</div>
                        <div class="text-gray-700 font-mono font-bold">
                          {{ customer.bankDetails.accountNumber || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">IFSC Code</div>
                        <div class="text-blue-700 font-bold">
                          {{ customer.bankDetails.ifscCode || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Account Type</div>
                        <div class="text-gray-700">
                          {{ customer.bankDetails.accountType || 'N/A' }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Branch Name</div>
                        <div class="text-gray-700 uppercase">
                          {{ customer.bankDetails.branchName || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">MICR Code</div>
                        <div class="text-gray-700">
                          {{ customer.bankDetails.micrCode || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">GSTN Number</div>
                        <div class="text-gray-700">
                          {{ customer.bankDetails.gstnNumber || 'N/A' }}
                        </div>
                      </div>
                      <div
                        v-if="customer.bankDetails.phoneNo"
                        class="grid grid-cols-[160px_1fr] gap-4 mb-4"
                      >
                        <div class="font-bold text-gray-800">Phone No</div>
                        <div class="text-gray-700">{{ customer.bankDetails.phoneNo }}</div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Bank Address Section -->
                  <div v-if="customer.bankDetails.address" class="mt-4">
                    <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Bank Address</h4>
                    <div class="border rounded-lg p-4 bg-gray-50">
                      <div class="text-gray-700 leading-relaxed">
                        {{ extractAddress(customer.bankDetails.address) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <v-icon size="64" color="gray-300">mdi-bank</v-icon>
                  <p class="mt-4 text-gray-500 font-medium italic">No bank details provided.</p>
                </div>
              </v-card>
            </v-tab-item>
            <!-- 5: Loan Details -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <!-- <div class="flex justify-between items-center mb-6">
                  <h3 class="text-lg font-bold text-[#5c729c]">Loan History</h3>
                  <v-btn icon @click="fetchCustomerLoans" :loading="loadingLoans">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div> -->

                <div v-if="loadingLoans" class="flex justify-center py-12">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>

                <div v-else-if="groupedLoans && groupedLoans.length > 0">
                  <div v-for="group in groupedLoans" :key="group.subCaseId" class="mb-6">
                    <div class="d-flex align-center my-3">
                      <span class="text-sm font-bold text-blue-800 uppercase tracking-wider">{{
                        group.subCaseName
                      }}</span>
                      <v-divider class="ml-4"></v-divider>
                    </div>

                    <v-simple-table class="border rounded-lg overflow-hidden">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left font-bold text-gray-600">
                              {{ getFieldDisplayName(group.subCase, 'caseNo', 'Case No') }}
                            </th>
                            <th class="text-left font-bold text-gray-600">
                              {{ getFieldDisplayName(group.subCase, 'typeOfLoan', 'Type') }}
                            </th>
                            <th class="text-right font-bold text-gray-600">
                              {{
                                getFieldDisplayName(
                                  group.subCase,
                                  'loanAmountSanctioned',
                                  'Sanctioned Amt'
                                )
                              }}
                            </th>
                            <th class="text-center font-bold text-gray-600">
                              {{ getFieldDisplayName(group.subCase, 'tenure', 'Tenure') }}
                            </th>
                            <th class="text-center font-bold text-gray-600">
                              {{ getFieldDisplayName(group.subCase, 'status', 'Status') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="loan in group.loans"
                            :key="loan._id"
                            class="transition-all"
                            :class="loan.isDeleted ? 'bg-red-50/30 opacity-60' : 'hover:bg-gray-50'"
                          >
                            <td class="font-bold text-blue-700 text-lg px-2">
                              <span :class="{ 'line-through text-red-600': loan.isDeleted }">{{
                                loan.caseNo
                              }}</span>
                              <v-chip
                                v-if="loan.isDeleted"
                                x-small
                                color="red"
                                dark
                                label
                                class="ml-1 font-bold"
                                >Deleted</v-chip
                              >
                            </td>
                            <td :class="{ 'line-through opacity-50': loan.isDeleted }">
                              {{ loan.loanDetails?.typeOfLoan || loan.caseType }}
                            </td>
                            <td
                              class="text-right"
                              :class="{ 'line-through opacity-50': loan.isDeleted }"
                            >
                              ₹{{ (loan.loanDetails?.loanAmountSanctioned || 0).toLocaleString() }}
                            </td>
                            <td
                              class="text-center"
                              :class="{ 'line-through opacity-50': loan.isDeleted }"
                            >
                              {{ loan.loanDetails?.tenure || '-' }}
                              {{ loan.loanDetails?.tenureType || '' }}
                            </td>
                            <td class="text-center">
                              <v-chip
                                x-small
                                :color="
                                  getStatusInfo(loan.loanDetails?.currentStatus || loan.status)
                                    .color
                                "
                                text-color="white"
                                class="font-bold"
                              >
                                {{ loan.loanDetails?.currentStatus || loan.status }}
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </div>
                <div
                  v-else
                  class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <v-icon size="64" color="gray-300">mdi-text-box-search-outline</v-icon>
                  <p class="mt-4 text-gray-500 font-medium italic">
                    No loan records found for this customer.
                  </p>
                </div>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </div>

    <!-- Aadhaar OTP Dialog -->
    <v-dialog v-model="aadhaarOtpDialog" persistent max-width="420">
      <v-card class="pa-5 rounded-xl">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <v-icon color="primary" class="mr-2">mdi-shield-lock-outline</v-icon>
            <h3 class="text-base font-bold text-gray-800">Verify Aadhaar</h3>
          </div>
          <v-btn icon small @click="aadhaarOtpDialog = false" :disabled="aadhaarLoading">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="px-0">
          <p class="text-xs text-gray-600 mb-4">
            An OTP has been sent to the mobile number registered with this Aadhaar. Please enter the 6-digit OTP to complete verification.
          </p>
          <div class="flex justify-center my-4">
            <PasscodeInput
              v-if="aadhaarOtpDialog"
              v-model="aadhaarOtp"
              :otpField="6"
              :autoFocus="true"
              @keyup.enter.native="submitAadhaarOtp"
            />
          </div>
        </v-card-text>
        <v-card-actions class="px-0 pt-2">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            class="normal-case font-semibold"
            :disabled="aadhaarLoading"
            @click="aadhaarOtpDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="white--text normal-case font-semibold px-5 rounded-lg"
            :loading="aadhaarLoading"
            :disabled="!aadhaarOtp || aadhaarOtp.length !== 6"
            @click="submitAadhaarOtp"
          >
            Verify OTP
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getFullAddress, convertToTitleCase, maskPhoneNumber } from '@/utils/common'
import { axiosUrl } from '@/utils/workforceApi'
import PasscodeInput from '@/components/PasscodeInput.vue'

export default {
  name: 'CustomerDetailsCard',
  components: {
    PasscodeInput
  },
  props: {
    customer: Object,
    closeCustomerPopUp: Function
  },
  data() {
    return {
      activeTab: 0,
      verifyingIndex: null,
      customerLoans: [],
      loadingLoans: false,
      // Identity reveal state
      identityRevealed: false,
      loadingIdentity: false,
      identityData: [], // decrypted identity details from API
      avatarLoadError: false,
      fallbackAvatar: require('~/assets/img/genericImage.png'),
      // Aadhaar OTP Verification
      aadhaarOtpDialog: false,
      aadhaarOtp: '',
      aadhaarLoading: false,
      aadhaarRefId: null,
      aadhaarIndex: null
    }
  },
  computed: {
    avatarUrl() {
      if (this.customer?.crn && !this.avatarLoadError) {
        const baseUrl = process.env.VUE_APP_S3_BASE_URL
        const orgData = JSON.parse(localStorage.getItem('orgData')) || {}
        let orgId = orgData?.id || null
        //will remove this hard coded org id
        if (orgId === 112) {
          orgId = 115
        }
        // ||'https://zavronfinserv.s3.ap-south-1.amazonaws.com/41/Customer/'
        // console.log(`${baseUrl}${orgId}/customer/${this.customer.crn}.jpg`, '>>>>>')
        return `${baseUrl}${orgId}/customer/${this.customer.crn}.jpg`
      }
      return ``
    },
    totalLoansCount() {
      return this.allFilteredLoans.length
    },
    statusCounts() {
      const counts = {}
      this.allFilteredLoans.forEach((loan) => {
        const status = loan.loanDetails?.currentStatus || loan.status || 'Unknown'
        counts[status] = (counts[status] || 0) + 1
      })
      return counts
    },
    allFilteredLoans() {
      const routeUseCase = this.$route.params.useCase
      return this.customerLoans.filter((loan) => {
        return !routeUseCase || loan.useCase === routeUseCase
      })
    },
    groupedLoans() {
      const groups = {}
      const storeSubCases = this.$store.state.subCases || []
      this.allFilteredLoans.forEach((loan) => {
        let subCaseObj = loan.subCase
        let subCaseId = 'other'
        let subCaseName = 'Other Subcases'

        if (subCaseObj) {
          if (typeof subCaseObj === 'object' && subCaseObj !== null) {
            subCaseId = subCaseObj._id || 'other'
            subCaseName = subCaseObj.name || 'Other Subcases'
          } else {
            subCaseId = subCaseObj
            const matched = storeSubCases.find((sc) => sc._id === subCaseId)
            subCaseName = matched?.name || 'Other Subcases'
            subCaseObj = matched || { _id: subCaseId, name: subCaseName }
          }
        }

        const key = `${subCaseId}_${subCaseName}`
        if (!groups[key]) {
          groups[key] = {
            subCaseId,
            subCaseName,
            subCase: subCaseObj,
            loans: []
          }
        }
        groups[key].loans.push(loan)
      })
      return Object.values(groups)
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 5 && this.customerLoans.length === 0) {
        this.fetchCustomerLoans()
      }
    },
    customer: {
      handler(newVal) {
        if (newVal) {
          this.customerLoans = []
          if (this.activeTab === 5) {
            this.fetchCustomerLoans()
          }
          // Reset identity reveal when customer changes
          this.identityRevealed = false
          this.identityData = []
          this.avatarLoadError = false
        }
      },
      immediate: true
    }
  },
  methods: {
    getFieldDisplayName(subCase, fieldKey, defaultName) {
      if (!subCase) return defaultName
      const mappings = {
        ...subCase.baseFieldMappings,
        ...subCase.fieldMappings,
        ...subCase.collectionManagementFieldMapping,
        ...subCase.loanManagementFieldMapping,
        ...subCase.customerFieldMappings
      }
      return mappings[fieldKey]?.displayName || defaultName
    },
    // Fetch decrypted identity numbers on demand
    async fetchIdentityDetails() {
      if (!this.customer?._id) return
      try {
        this.loadingIdentity = true
        const response = await this.$axios.get(`/customer/${this.customer._id}/identity-details`)
        this.identityData = response.data?.data?.identityDetails || []
      } catch (error) {
        console.error('Failed to fetch identity details:', error)
        this.$toast?.error('Could not load identity details. Please try again.')
      } finally {
        this.loadingIdentity = false
      }
    },
    async toggleIdentityReveal() {
      if (this.identityRevealed) {
        // Hide: clear decrypted data from memory
        this.identityRevealed = false
        this.identityData = []
      } else {
        // Fetch then reveal
        await this.fetchIdentityDetails()
        if (this.identityData.length > 0) {
          this.identityRevealed = true
        }
      }
    },
    // Return the decrypted idNumber for a given idType
    getRevealedId(idType) {
      const match = this.identityData.find(
        (d) => (d.idType || '').toLowerCase() === (idType || '').toLowerCase()
      )
      return match?.idNumber || null
    },
    formatMaskedId(idNumber) {
      if (!idNumber) return '••••••••'
      const clean = String(idNumber).trim()
      if (clean.includes('•')) return clean
      if (clean.length <= 4) return clean
      const first2 = clean.slice(0, 2)
      const last2 = clean.slice(-2)
      const maskLength = Math.max(clean.length - 4, 4)
      return `${first2}${'•'.repeat(maskLength)}${last2}`
    },
    async fetchCustomerLoans() {
      if (!this.customer) return

      let subCases = this.$store.state.subCases || []
      if (!subCases.length) {
        try {
          const res = await this.$axios.get('/orgSetup/extend-usecase')
          subCases = res.data?.data || []
          this.$store.dispatch('setSubCases', subCases)
        } catch (e) {
          console.error('Error pre-fetching subCases:', e)
        }
      }

      try {
        this.loadingLoans = true
        const customerName =
          this.customer.customerFullName ||
          `${this.customer.firstName || ''} ${this.customer.lastName || ''}`.trim()

        const response = await this.$axios.get('/collection/loan', {
          params: {
            customerSearch: this.customer.crn,
            limit: 100
          }
        })
        this.customerLoans = response.data?.loans || []
      } catch (error) {
        console.error('Error fetching customer loans:', error)
      } finally {
        this.loadingLoans = false
      }
    },
    onAvatarError() {
      this.avatarLoadError = true
    },

    openImage(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    getFileIconInfo(ext) {
      const extension = (ext || '').toLowerCase()
      if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extension)) {
        return { icon: 'mdi-file-image-outline', color: 'blue' }
      } else if (extension === 'pdf') {
        return { icon: 'mdi-file-pdf-box', color: 'red' }
      } else if (['doc', 'docx'].includes(extension)) {
        return { icon: 'mdi-file-word-outline', color: 'indigo' }
      } else if (['xls', 'xlsx'].includes(extension)) {
        return { icon: 'mdi-file-excel-outline', color: 'green' }
      }
      return { icon: 'mdi-file-document-outline', color: 'grey' }
    },
    isImageFile(ext) {
      return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes((ext || '').toLowerCase())
    },
    getCustomerDocUrl(id) {
      if (id.docUrl) return id.docUrl
      const orgData = JSON.parse(localStorage.getItem('orgData')) || {}
      let orgId = orgData?.id || null
      //will remove this hard coded org id
      if (orgId === 112) {
        orgId = 115
      }
      // const baseUrl = 'https://zavronfinserv.s3.ap-south-1.amazonaws.com/'
      const baseUrl = process.env.VUE_APP_S3_BASE_URL
      // console.log(`${baseUrl}41/customer/${this.customer.crn}/19/${id.docName}`)
      return `${baseUrl}${orgId}/customer/${this.customer.crn}/19/${id.docName}`
    },
    getDocExt(docName) {
      return (docName || '').split('.').pop().toLowerCase()
    },
    extractAddress(str) {
      if (!str) return 'N/A'
      return getFullAddress(str)
    },
    convertTitle(str) {
      if (str) {
        return convertToTitleCase(str)
      }
      return 'N/A'
    },
    mask(num) {
      if (!num) return ''
      return maskPhoneNumber(num)
    },
    formatDate(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        return d
          .toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          .replace(/\//g, '-')
      } catch (e) {
        return date
      }
    },
    formatDateTime(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        const dateStr = d
          .toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          .replace(/\//g, '-')
        const timeStr = d.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
        return `${dateStr} ${timeStr}`
      } catch (e) {
        return date
      }
    },
    formatUserName(userObj) {
      if (!userObj) return 'System'
      const systemUser = userObj.systemUserId || userObj
      const firstName = systemUser.firstName || ''
      const lastName = systemUser.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || 'N/A'
    },
    calculateAge(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        const ageDifMs = Date.now() - d.getTime()
        const ageDate = new Date(ageDifMs)
        return Math.abs(ageDate.getUTCFullYear() - 1970)
      } catch (e) {
        return 'N/A'
      }
    },
    openGoogleMaps(address) {
      const fullAddress = getFullAddress(address)
      const encodedAddress = encodeURIComponent(fullAddress)
      const googleMapsURL = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
      window.open(googleMapsURL, '_blank')
    },
    handleClose() {
      this.activeTab = 0
      if (this.closeCustomerPopUp) {
        this.closeCustomerPopUp()
      }
    },
    async verifyIdentity(index) {
      if (!this.customer?._id) return

      try {
        this.verifyingIndex = index
        const idDetail = this.customer.identityDetails[index]
        const user = this.$storage.getUniversal('user')
        const userName =
          user?.fullName ||
          (user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Admin')

        let verifiedBy = userName.trim()
        let nameAsperId = idDetail.nameAsperId
        let issueDate = idDetail.issueDate
        let issuedBy = idDetail.issuedBy

        // If it's a GSTIN, verify via Decentro API
        if ((idDetail.idType || '').toUpperCase() === 'GSTIN') {
          let gstinNumber = this.getRevealedId('GSTIN')
          if (!gstinNumber || gstinNumber.includes('•') || gstinNumber.includes('XX')) {
            await this.fetchIdentityDetails()
            gstinNumber =
              this.getRevealedId('GSTIN') ||
              (idDetail.idNumber && !idDetail.idNumber.includes('•') && !idDetail.idNumber.includes('XX')
                ? idDetail.idNumber
                : null)
          }

          if (!gstinNumber || gstinNumber.length < 15 || gstinNumber.includes('•') || gstinNumber.includes('XX')) {
            this.$toast?.error('Cannot verify: GSTIN is masked or invalid. Please edit customer to set GSTIN.')
            return
          }

          const res = await this.$axios.post('/customer/verify-gst', {
            gstin: gstinNumber.trim().toUpperCase()
          })

          if (res.data?.success && res.data?.data && res.data.data.success !== false) {
            const gData = res.data.data
            verifiedBy = gData.legalName
              ? `Decentro GST (${gData.legalName})`
              : 'Decentro GST / System'
            nameAsperId = gData.legalName || nameAsperId
            issueDate = gData.registrationDate ? new Date(gData.registrationDate) : issueDate
            issuedBy = 'GSTN / Government of India'
          } else {
            this.$toast?.error(res.data?.data?.message || res.data?.message || 'GSTIN verification failed')
            return
          }
        } else if ((idDetail.idType || '').toUpperCase() === 'PAN') {
          // If it's a PAN, verify via Decentro API
          let panNumber = this.getRevealedId('PAN')
          if (!panNumber || panNumber.includes('•') || panNumber.includes('XX')) {
            await this.fetchIdentityDetails()
            panNumber =
              this.getRevealedId('PAN') ||
              (idDetail.idNumber && !idDetail.idNumber.includes('•') && !idDetail.idNumber.includes('XX')
                ? idDetail.idNumber
                : null)
          }

          if (!panNumber || panNumber.length < 10 || panNumber.includes('•') || panNumber.includes('XX')) {
            this.$toast?.error('Cannot verify: PAN is masked or invalid. Please edit customer to set PAN.')
            return
          }

          const res = await this.$axios.post('/customer/verify-pan', {
            pan: panNumber.trim().toUpperCase()
          })

          if (res.data?.success && res.data?.data && res.data.data.success !== false) {
            const pData = res.data.data
            verifiedBy = pData.name
              ? `Decentro PAN (${pData.name})`
              : 'Decentro PAN / System'
            nameAsperId = pData.name || nameAsperId
            issuedBy = 'Income Tax Department / Government of India'
          } else {
            this.$toast?.error(res.data?.data?.message || res.data?.message || 'PAN verification failed')
            return
          }
        } else if (['AADHAAR', 'AADHAR'].includes((idDetail.idType || '').toUpperCase())) {
          // If it's Aadhaar, trigger OTP verification flow
          let aadhaarNumber = this.getRevealedId(idDetail.idType)
          if (!aadhaarNumber || aadhaarNumber.includes('•') || aadhaarNumber.includes('XX')) {
            await this.fetchIdentityDetails()
            aadhaarNumber =
              this.getRevealedId(idDetail.idType) ||
              (idDetail.idNumber && !idDetail.idNumber.includes('•') && !idDetail.idNumber.includes('XX')
                ? idDetail.idNumber
                : null)
          }

          const cleanAadhaar = String(aadhaarNumber || '').replace(/\D/g, '')
          if (!cleanAadhaar || cleanAadhaar.length !== 12) {
            this.$toast?.error(
              'Cannot verify: Aadhaar is masked or invalid (must be 12 digits). Please edit customer to set Aadhaar.'
            )
            return
          }

          const res = await this.$axios.post('/customer/aadhaar/otp', {
            aadhaarNumber: cleanAadhaar
          })

          if (res.data?.success && res.data?.data) {
            this.aadhaarRefId = res.data.data.reference_id || res.data.data.referenceId
            this.aadhaarOtp = ''
            this.aadhaarIndex = index
            this.aadhaarOtpDialog = true
            this.$toast?.info('OTP sent to Aadhaar-registered mobile number.')
            return
          } else {
            this.$toast?.error(
              res.data?.data?.message || res.data?.message || 'Failed to send Aadhaar OTP'
            )
            return
          }
        } else {
          this.$toast?.error(`Verification is not currently supported for ${idDetail.idType || 'this document type'}.`)
          return
        }

        // We perform a deep clone of the customer object and update the identityDetails
        const customerPayload = JSON.parse(JSON.stringify(this.customer))
        customerPayload.identityDetails[index].isVerfied = true
        customerPayload.identityDetails[index].verifiedBy = verifiedBy
        customerPayload.identityDetails[index].verifiedOn = new Date()
        if (nameAsperId) customerPayload.identityDetails[index].nameAsperId = nameAsperId
        if (issueDate) customerPayload.identityDetails[index].issueDate = issueDate
        if (issuedBy) customerPayload.identityDetails[index].issuedBy = issuedBy

        await this.$axios.put(
          `${axiosUrl.customers.postCustomer}/${this.customer._id}`,
          customerPayload
        )

        // Update local state (reactive update)
        this.$set(this.customer.identityDetails, index, customerPayload.identityDetails[index])
        this.$toast?.success(`${idDetail.idType} verified successfully!`)
      } catch (error) {
        console.error('Verification failed', error)
        this.$toast?.error(
          error.response?.data?.message || error.message || 'Failed to verify identity document.'
        )
      } finally {
        this.verifyingIndex = null
      }
    },
    async submitAadhaarOtp() {
      if (
        !this.aadhaarOtp ||
        this.aadhaarOtp.length !== 6 ||
        !this.aadhaarRefId ||
        this.aadhaarIndex === null
      ) {
        return
      }

      this.aadhaarLoading = true
      try {
        const index = this.aadhaarIndex
        const idDetail = this.customer.identityDetails[index]
        const res = await this.$axios.post('/customer/aadhaar/verify', {
          referenceId: this.aadhaarRefId,
          otp: this.aadhaarOtp.trim()
        })

        if (res.data?.success && res.data?.data && res.data.data.success !== false) {
          const aData = res.data.data
          const verifiedBy = aData.name
            ? `Decentro Aadhaar (${aData.name})`
            : 'Decentro Aadhaar / System'
          const nameAsperId = aData.name || idDetail.nameAsperId
          const issueDate = aData.dob ? new Date(aData.dob) : idDetail.issueDate
          const issuedBy = 'UIDAI / Government of India'

          const customerPayload = JSON.parse(JSON.stringify(this.customer))
          customerPayload.identityDetails[index].isVerfied = true
          customerPayload.identityDetails[index].verifiedBy = verifiedBy
          customerPayload.identityDetails[index].verifiedOn = new Date()
          if (nameAsperId) customerPayload.identityDetails[index].nameAsperId = nameAsperId
          if (issueDate) customerPayload.identityDetails[index].issueDate = issueDate
          if (issuedBy) customerPayload.identityDetails[index].issuedBy = issuedBy

          await this.$axios.put(
            `${axiosUrl.customers.postCustomer}/${this.customer._id}`,
            customerPayload
          )

          this.$set(this.customer.identityDetails, index, customerPayload.identityDetails[index])
          this.aadhaarOtpDialog = false
          this.$toast?.success('Aadhaar verified successfully!')
        } else {
          this.$toast?.error(
            res.data?.data?.message || res.data?.message || 'Aadhaar verification failed'
          )
        }
      } catch (error) {
        console.error('Aadhaar verification failed', error)
        this.$toast?.error(
          error.response?.data?.message || error.message || 'Failed to verify Aadhaar OTP.'
        )
      } finally {
        this.aadhaarLoading = false
      }
    },
    getStatusInfo(status) {
      const statusMap = {
        Applied: { color: 'blue', icon: 'mdi-file-plus', bgColorClass: 'bg-blue-50' },
        'Under Review': { color: 'amber', icon: 'mdi-magnify', bgColorClass: 'bg-amber-50' },
        Sanctioned: { color: 'indigo', icon: 'mdi-check-circle', bgColorClass: 'bg-indigo-50' },
        'Disbursal Initiated': {
          color: 'cyan',
          icon: 'mdi-bank-transfer-out',
          bgColorClass: 'bg-cyan-50'
        },
        Disbursed: { color: 'teal', icon: 'mdi-bank-transfer', bgColorClass: 'bg-teal-50' },
        Active: { color: 'green', icon: 'mdi-check-decagram', bgColorClass: 'bg-green-50' },
        Closed: { color: 'grey', icon: 'mdi-lock', bgColorClass: 'bg-gray-50' },
        Defaulted: { color: 'red', icon: 'mdi-alert', bgColorClass: 'bg-red-50' },
        NPA: { color: 'deep-orange', icon: 'mdi-alert-octagon', bgColorClass: 'bg-orange-50' },
        'Written Off': { color: 'brown', icon: 'mdi-delete-sweep', bgColorClass: 'bg-brown-50' },
        Foreclosed: { color: 'red darken-4', icon: 'mdi-bank-off', bgColorClass: 'bg-red-50' },
        Deleted: { color: 'red darken-2', icon: 'mdi-delete-forever', bgColorClass: 'bg-red-100' },
        Open: { color: 'success', icon: 'mdi-folder-open', bgColorClass: 'bg-green-50' },
        Completed: { color: 'primary', icon: 'mdi-check-all', bgColorClass: 'bg-blue-50' },
        PendingApprovals: {
          color: 'orange',
          icon: 'mdi-clock-alert',
          bgColorClass: 'bg-orange-50'
        },
        Rejected: { color: 'error', icon: 'mdi-close-circle', bgColorClass: 'bg-red-50' },
        Settled: { color: 'deep-purple', icon: 'mdi-currency-usd', bgColorClass: 'bg-purple-50' },
        Canceled: { color: 'grey darken-3', icon: 'mdi-cancel', bgColorClass: 'bg-gray-100' }
      }
      return (
        statusMap[status] || {
          color: 'grey',
          icon: 'mdi-help-circle',
          bgColorClass: 'bg-gray-50'
        }
      )
    }
  }
}
</script>

<style scoped>
.customer-profile-container {
  max-width: 100%;
}

.customer-profile-container .v-tab {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: normal;
  color: #5c729c !important;
}

.customer-profile-container .v-tab--active {
  color: #1976d2 !important;
  font-weight: 700;
}
</style>
